import { useQuery } from "@apollo/client";
import { useState } from "react";
import { GET_CATEGORY_BY_PARENTID } from "@/core/graphql/queries/getCategories";
import { Category } from "@/core/models/categories/category";
import { getVehicleWheelFitments, formatWheelSize, WheelSize } from "@/lib/wheelSize";

interface VehicleCategoryData {
  getMstCategoryByParentId: { result: Category[] | null } | null;
}

export default function VehicleWheelSelector({ onApply, onVehicleChange }: {
  onApply: (size: WheelSize, vehicle: string) => void;
  onVehicleChange: () => void;
}) {
  const [path, setPath] = useState<Category[]>([]);
  const [fitmentId, setFitmentId] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const parentId = path.length ? path[path.length - 1].categoryId : 2012;
  const { data, loading, error, refetch } = useQuery<VehicleCategoryData, { id: number; size: number }>(
    GET_CATEGORY_BY_PARENTID,
    { variables: { id: parentId, size: 1000 }, notifyOnNetworkStatusChange: true }
  );
  const categories = (data?.getMstCategoryByParentId?.result ?? []).filter((category) => category.isActive !== false);
  const fitments = getVehicleWheelFitments(path.map((category) => category.categoryName));
  const selected = fitments.find((fitment) => fitment.id === fitmentId);
  const invalidResponse = !loading && !error && !Array.isArray(data?.getMstCategoryByParentId?.result);

  function changePath(next: Category[]) {
    setPath(next);
    setFitmentId("");
    setConfirmed(false);
    onVehicleChange();
  }

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-6" aria-labelledby="vehicle-title">
      <h2 id="vehicle-title" className="text-xl font-bold">Find a reference size by vehicle</h2>
      <p className="mt-2 text-sm text-slate-600">Choose your make and model from our vehicle directory, then select a listed year and trim. Not every vehicle has a reference mapping yet.</p>
      <nav aria-label="Vehicle selection" className="my-4 flex flex-wrap gap-2 text-sm">
        <button type="button" className="text-blue-700 underline" onClick={() => changePath([])}>All vehicles</button>
        {path.map((category, index) => (
          <button type="button" key={category.categoryId} className="text-blue-700 underline" onClick={() => changePath(path.slice(0, index + 1))}>
            / {category.categoryName}
          </button>
        ))}
      </nav>
      {loading && <p role="status">Loading vehicles...</p>}
      {(error || invalidResponse) && (
        <div role="alert" className="rounded-lg bg-red-50 p-3 text-red-800">
          <p>Unable to load the vehicle directory. You can still enter your placard size manually below.</p>
          <button type="button" className="mt-2 underline" onClick={() => { void refetch(); }}>Retry vehicle loading</button>
        </div>
      )}
      {!loading && !error && !invalidResponse && categories.length > 0 && (
        <label className="block text-sm font-medium">
          {path.length ? "Select the next vehicle detail" : "Select vehicle make"}
          <select className="mt-2 w-full rounded-lg border border-slate-300 p-3" value="" onChange={(event) => {
            const category = categories.find((item) => item.categoryId === Number(event.target.value));
            if (category) changePath([...path, category]);
          }}>
            <option value="">Choose a vehicle...</option>
            {categories.map((category) => <option key={category.categoryId} value={category.categoryId}>{category.categoryName}</option>)}
          </select>
        </label>
      )}
      {!loading && !error && !invalidResponse && categories.length === 0 && path.length === 0 && <p role="status">No vehicles are available in the directory right now. Enter a tyre size manually below.</p>}
      {path.length > 0 && fitments.length === 0 && !loading && (
        <p role="status" className="mt-3 text-sm text-slate-600">No reference tyre size is mapped for this selection. {categories.length > 0 ? "Choose a more specific model, or " : ""}enter the size from your vehicle placard below. We do not guess a size.</p>
      )}
      {fitments.length > 0 && (
        <div className="mt-4 space-y-3">
          <label className="block text-sm font-medium">
            Reference year / trim / wheel option
            <select className="mt-2 w-full rounded-lg border border-slate-300 p-3" value={fitmentId} onChange={(event) => {
              setFitmentId(event.target.value);
              setConfirmed(false);
              onVehicleChange();
            }}>
              <option value="">Choose your exact variant...</option>
              {fitments.map((fitment) => <option key={fitment.id} value={fitment.id}>{fitment.variant} - {formatWheelSize(fitment.size)}</option>)}
            </select>
          </label>
          {selected && (
            <>
              <p className="font-semibold">Reference size: {formatWheelSize(selected.size)}</p>
              <p className="text-sm text-amber-800">Curated reference, not a VIN-verified fitment. Factory options, market, axle, load index and speed rating may differ. Check the door placard or owner&apos;s manual before using this size.</p>
              <label className="flex items-start gap-2 text-sm">
                <input type="checkbox" className="mt-1" checked={confirmed} onChange={(event) => { setConfirmed(event.target.checked); onVehicleChange(); }} />
                This year/trim and tyre size match my vehicle&apos;s placard.
              </label>
              <button type="button" disabled={!confirmed} className="rounded-lg bg-blue-700 px-4 py-3 font-semibold text-white disabled:opacity-40" onClick={() => onApply(selected.size, `${selected.make} ${selected.model} ${selected.variant}`)}>Use as original size</button>
            </>
          )}
        </div>
      )}
    </section>
  );
}
